import { useEffect, useMemo, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { FieldAttributes, useFormikContext, useField, Field } from 'formik';
import {
    Box,
    createFilterOptions,
    FilterOptionsState,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { mdiPlus, mdiSync } from '@mdi/js';

import {
    LocationsDocument,
    ILocationFragment,
    ILocationsQueryVariables,
    ILocationStatus,
} from 'graphql/types';
import { Autocomplete } from 'common/components/FormField';
import { useApolloError } from 'common/hooks/useApolloError';
import { getLocationStatusString } from 'common/utils/location';
import { Chip } from 'common/components/Chip';
import { IconButton } from 'common/components/IconButton';
import { Icon } from 'common/components/Icon';
import { ActionButton } from 'common/components/ActionButton';

interface ILocationOption extends Partial<ILocationFragment> {
    inputValue?: string;
}

const filter = createFilterOptions<ILocationOption>();

type TAvailabilityFilters = Pick<
    ILocationsQueryVariables,
    'availabilityStartDate' | 'availabilityEndDate'
>;

interface IProps {
    allowCreateNew?: boolean;
    allowCreateString?: boolean;
    label?: string;
    disableClearable?: boolean;
    allowRefetch?: boolean;
    availabilityFilters?: TAvailabilityFilters;
    onClickNewLocation?(): void;
}

export const LocationSelectField = ({
    name,
    label,
    availabilityFilters,
    disableClearable,
    allowRefetch,
    allowCreateNew,
    allowCreateString,
    onClickNewLocation,
    ...other
}: IProps & FieldAttributes<unknown>) => {
    const [translate] = useTranslation();
    const { showApolloError } = useApolloError();

    const prevFilters = useRef<TAvailabilityFilters | undefined>(
        availabilityFilters
    );
    const [filtersChanged, setFiltersChanged] = useState(false);

    const [fetchLocations, { data, loading, error }] = useLazyQuery(
        LocationsDocument,
        {
            fetchPolicy: 'no-cache',
        }
    );

    useEffect(() => {
        if (error) {
            showApolloError(error);
        }
    }, [error, showApolloError]);

    useEffect(() => {
        if (data) {
            setFiltersChanged(false);
        }
    }, [data]);

    const locations = (data?.locations || []).filter(
        (location): location is ILocationFragment =>
            location !== undefined && location.id !== undefined
    );
    const [field] = useField<ILocationOption | undefined>(name);

    const { setFieldValue } = useFormikContext();

    const locationOptions: ILocationOption[] = useMemo(
        () =>
            locations.map((location) => ({
                id: location.id,
                title: location.title,
                location: location.location,
                status: location.status,
                description: location.description,
            })),
        [locations]
    );

    useEffect(() => {
        if (!availabilityFilters) return;

        if (
            JSON.stringify(availabilityFilters) ===
            JSON.stringify(prevFilters.current)
        ) {
            return;
        }

        setFiltersChanged(true);
        prevFilters.current = availabilityFilters;
    }, [availabilityFilters]);

    // Get status of selected location
    const selectedLocationStatus = locationOptions.find(
        (option) => field.value?.id === option.id
    )?.status;

    const handleChange = (
        _event: React.ChangeEvent,
        value?: ILocationOption
    ) => {
        const newValue = value;

        // If inputValue is set we want to use that as title, this happens when a value is created via the
        // filterOptions function, because title is a translated value
        if (newValue?.inputValue) {
            newValue.title = newValue.inputValue;
        }

        setFieldValue(name, newValue);
    };

    const handleFilterOptions = (
        options: ILocationOption[],
        params: FilterOptionsState<Partial<ILocationFragment>>
    ) => {
        const filtered = filter(options, params);

        const { inputValue } = params;

        // Suggest the creation of a new value
        const isExisting = options.some(
            (option) => inputValue === option.title
        );

        if (!!inputValue && !isExisting) {
            filtered.push({
                inputValue: inputValue,
                title: `${translate('useLocationInputValue')} "${inputValue}"`,
            });
        }

        return filtered;
    };

    let onChange;
    let onFilterOptions;

    if (allowCreateString && !allowCreateNew) {
        onChange = handleChange;
        onFilterOptions = handleFilterOptions;
    }

    return (
        <>
            <Box sx={{ display: 'flex' }}>
                <Field
                    clearOnBlur
                    selectOnFocus
                    component={Autocomplete}
                    defaultValue=""
                    disableClearable={disableClearable}
                    endAdornment={
                        error || (allowRefetch && filtersChanged) ? (
                            <IconButton
                                iconPath={mdiSync}
                                iconSize="2.2rem"
                                onClick={() => {
                                    fetchLocations({
                                        variables: availabilityFilters,
                                    });
                                }}
                            />
                        ) : (
                            selectedLocationStatus &&
                            selectedLocationStatus !==
                                ILocationStatus.Unknown && (
                                <Chip
                                    label={getLocationStatusString(
                                        selectedLocationStatus
                                    )}
                                />
                            )
                        )
                    }
                    filterOptions={onFilterOptions}
                    freeSolo={!!allowCreateString && !allowCreateNew}
                    getOptionLabel={(option: ILocationOption) => {
                        // Value selected with enter, right from the input
                        if (typeof option === 'string') {
                            return option;
                        }

                        if (option.inputValue) {
                            return option.inputValue;
                        }

                        return option.title;
                    }}
                    InputProps={{
                        label,
                    }}
                    isOptionEqualToValue={(
                        option: ILocationOption,
                        value: ILocationOption
                    ) => {
                        if (option.id) {
                            return option.id === value.id;
                        }

                        return option.title === value.title;
                    }}
                    loading={loading}
                    name={name}
                    options={locationOptions}
                    renderOption={(
                        props: React.HTMLAttributes<HTMLLIElement>,
                        option: ILocationOption
                    ) => (
                        <Box component="li" {...props} key={option.id}>
                            <Box>
                                <Box>
                                    <Typography
                                        sx={{ fontWeight: 700 }}
                                        variant="body1"
                                    >
                                        {option.title}
                                    </Typography>
                                </Box>
                                {option.location && (
                                    <Box>
                                        <Typography variant="body2">
                                            {option.location}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                            {!!option.status &&
                                option.status !== ILocationStatus.Unknown && (
                                    <Box sx={{ ml: 'auto' }}>
                                        <Chip
                                            label={getLocationStatusString(
                                                option.status
                                            )}
                                        />
                                    </Box>
                                )}
                        </Box>
                    )}
                    sx={[
                        { width: '100%' },
                        allowCreateNew ? { mr: 2 } : { mr: 0 },
                    ]}
                    onChange={onChange}
                    onOpen={() => {
                        // Only refetch locations when filters are changed
                        if (
                            !error &&
                            data &&
                            (!filtersChanged || !allowRefetch)
                        ) {
                            return;
                        }

                        fetchLocations({ variables: availabilityFilters });
                    }}
                    {...other}
                />

                {allowCreateNew && (
                    <ActionButton
                        outlined
                        aria-label={translate('moduleForm.meeting.newTitle')}
                        sx={{ ml: 'auto', flexShrink: 0, mt: '21px' }}
                        onClick={() => onClickNewLocation?.()}
                    >
                        <Icon path={mdiPlus} />
                    </ActionButton>
                )}
            </Box>
        </>
    );
};
